import { DOCUMENT } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EmbeddedViewRef, Inject, OnDestroy, QueryList, TemplateRef, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { Account, AccountFeatures, IAccount } from '@services/accounts.service';
import { LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service';
import { OrganisationDataService, IOrganisation, Organisation, OrganisationDashboard, OrganisationDataModelNode, OrganisationsHierarchy } from '@services/organisation-data.service';
import { combineLatest, takeUntil, distinctUntilChanged, filter, Subject, finalize } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { DashboardLayoutComponent, PanelModel } from '@syncfusion/ej2-angular-layouts';
import { TreeDashboardDrag } from '../../hierarchy/management/organisation-data-model/organisation-data-model.component';
import { DashboardPanelTemplateComponent, DashboardPanelTemplateInputData } from 'src/app/dialogs/dashboard-panel-template/dashboard-panel-template.component';
import { ChartComponent, FontModel } from '@syncfusion/ej2-angular-charts';
import { CircularGaugeComponent } from '@syncfusion/ej2-angular-circulargauge';
import { DataPoint } from '@services/datapoints-data.service';
import { ListViewComponent } from '@syncfusion/ej2-angular-lists';
import { TabComponent } from '@syncfusion/ej2-angular-navigations';
import { EditDashboardPanelComponent, EditDashboardPanelDialogData } from '../../../dialogs/edit-dashboard-panel/edit-dashboard-panel.component';
import { IWebhookIntegration } from '@services/integrations.service';

/**
 * 4 corners of panel position in x,y format
 */
export class PanelPosition {
	topLeft: number[];
	topRight: number[];
	bottomLeft: number[];
	bottomRight: number[];
	id?: string
}

@Component({
	selector: 'app-dashboard-creation',
	templateUrl: './dashboard-creation.component.html',
	styleUrls: ['./dashboard-creation.component.scss']
})
export class DashboardCreationComponent implements AfterViewInit, OnDestroy {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Selected Organisation ID */
	public organisationUID: string;

	/** Selected Organisation */
	public organisation: Organisation;

	/** accountID */
	public accountGUID: string;

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** this user */
	public thisUser: IWebAppUser;

	/** cellSpacing */
	public cellSpacing: number[] = [10, 10];

	/** aspectRatio */
	public aspectRatio: any = 1.8;
	// public aspectRatio: any = 100 / 72;

	/** dashboard */
	@ViewChild('dashboardLayout') dashboard!: DashboardLayoutComponent

	/** defaultPanelTemplate */
	@ViewChild('defaultPanelTemplate') defaultPanelTemplate: TemplateRef<any>;

	/** textPanelTemplate */
	@ViewChild('textPanelTemplate') textPanelTemplate: TemplateRef<any>;

	/** gaugePanelTemplate */
	@ViewChild('gaugePanelTemplate') gaugePanelTemplate: TemplateRef<any>;

	/** chartPanelTemplate */
	@ViewChild('chartPanelTemplate') chartPanelTemplate: TemplateRef<any>;

	/** listPanelTemplate */
	@ViewChild('listPanelTemplate') listPanelTemplate: TemplateRef<any>;

	/** shortcutPanelTemplate */
	@ViewChild('shortcutPanelTemplate') shortcutPanelTemplate: TemplateRef<any>;

	/** watchListPanelTemplate */
	@ViewChild('watchListPanelTemplate') watchListPanelTemplate: TemplateRef<any>;

	/** actionsPanelTemplate */
	@ViewChild('actionsPanelTemplate') actionsPanelTemplate: TemplateRef<any>;

	/** rulesPanelTemplate */
	@ViewChild('rulesPanelTemplate') rulesPanelTemplate: TemplateRef<any>;

	/** eventsPanelTemplate */
	@ViewChild('eventsPanelTemplate') eventsPanelTemplate: TemplateRef<any>;

	/** webhooksPanelTemplate */
	@ViewChild('webhooksPanelTemplate') webhooksPanelTemplate: TemplateRef<any>;

	/** eventsPanelTemplate */
	@ViewChild('linearGaugePanelTemplate') linearGaugePanelTemplate: TemplateRef<any>;

	/** annotatedGaugePanelTemplate */
	@ViewChild('annotatedGaugePanelTemplate') annotatedGaugePanelTemplate: TemplateRef<any>;

	/** pieChartPanelTemplate */
	@ViewChild('pieChartPanelTemplate') pieChartPanelTemplate: TemplateRef<any>;

	/** maxRows */
	public maxRows: number = 10;

	/** maxColumns */
	public maxColumns: number = 10;

	/** panelIDIndex */
	public panelIDIndex: number = 0;

	/** allGauges */
	@ViewChildren('gauge') allGauges!: QueryList<CircularGaugeComponent>;

	/** allGauges */
	@ViewChildren('chart') allCharts!: QueryList<ChartComponent>;

	/** allLists */
	@ViewChildren('list') allLists!: QueryList<ListViewComponent>;

	/** Chart Legend */
	public legend: Object;

	/** Chart Zoom Settings */
	public zoomSettings: Object;

	/** crosshair */
	public crosshair: object = {
		enable: true,
		lineType: 'Vertical'
	};

	/** Sensor pen Pallet Colours */
	public palette: object;

	/** Tooltip Settings */
	public tooltipSettings: Object;

	/** Chart boarder Settings */
	public borderSettings: Object;

	/**
	 * Chart Selection mode
	 * None|Point|Series|Cluster|DragXY|DragX|DragY
	 */
	public selectionMode: string;

	/** Sensor X Axis */
	public sensorXAxis: Object;

	/** titleStyle */
	public titleStyle: FontModel = {
		fontWeight: "bold",
		size: "large"
	}

	/** templates */
	public templates: TemplateRef<any>[] = [];

	/** hierarchyTemplates */
	public hierarchyTemplates: TemplateRef<any>[] = [];

	/** templatesTypes */
	public templateTypes: { text: string, value: number }[] = [
		{ text: "Text", value: 0 },
		{ text: "Gauge", value: 1 },
		{ text: "Chart", value: 2 },
		{ text: "List", value: 3 },
		{ text: "Shortcut", value: 4 },
		{ text: "Linear Gauge", value: 5 },
		{ text: "Annotated Gauge", value: 6 },
		{ text: "Pie Chart", value: 7 }
	];

	/** hierarchyTemplateTypes */
	public hierarchyTemplateTypes: { text: string, value: number }[] = [
		{ text: "Text", value: 0 },
		{ text: "Gauge", value: 1 },
		{ text: "Chart", value: 2 },
		{ text: "List", value: 3 },
		{ text: "Shortcut", value: 4 },
		{ text: "Watch List", value: 5 },
		{ text: "Actions", value: 6 },
		{ text: "Rules", value: 7 },
		{ text: "Events", value: 8 },
		{ text: "Linear Gauge", value: 9 },
		{ text: "Annotated Gauge", value: 10 },
		{ text: "Webhooks", value: 11 }
	];

	/** templateMetrics */
	public templateMetrics: { template: number, metrics: { text: string, value: number }[] }[] = [
		{
			template: 0, metrics: [
				{ text: "Period Average", value: 0 },
				{ text: "Running Average", value: 1 },
				{ text: "Live Usage", value: 2 },
				{ text: "Period Usage", value: 3 },
				{ text: "Max Usage", value: 4 }
			]
		},
		{
			template: 1, metrics: [
				{ text: "Low", value: 0 },
				{ text: "Normal", value: 1 },
				{ text: "High", value: 2 }
			]
		},
		{
			template: 2, metrics: [
				{ text: "Raw Value", value: 0 },
				{ text: "Usage", value: 1 },
			]
		},
		{
			template: 3, metrics: [
				{ text: "Period Average", value: 0 },
				{ text: "Running Average", value: 1 },
				{ text: "Live Usage", value: 2 },
				{ text: "Period Usage", value: 3 },
				{ text: "Max Usage", value: 4 }
			]
		},
		{
			template: 4, metrics: [
				{ text: "Shortcut Destination", value: 0 }
			]
		},
		{
			template: 5, metrics: [
				{ text: "Low", value: 0 },
				{ text: "Normal", value: 1 },
				{ text: "High", value: 2 }
			]
		},
		{
			template: 6, metrics: [
				{ text: "Low", value: 0 },
				{ text: "Normal", value: 1 },
				{ text: "High", value: 2 }
			]
		},
		{
			template: 7, metrics: [
				{ text: "Consumption", value: 0 }
			]
		}
	];

	/** hierarchyTemplateMetrics */
	public hierarchyTemplateMetrics: { template: number, metrics: { text: string, value: number }[] }[] = [
		{
			template: 0, metrics: [
				{ text: "Period Average", value: 0 },
				{ text: "Running Average", value: 1 },
				{ text: "Live Value", value: 2 },
				{ text: "Max Value", value: 3 }
			]
		},
		{
			template: 1, metrics: [
				{ text: "Low", value: 0 },
				{ text: "Normal", value: 1 },
				{ text: "High", value: 2 }
			]
		},
		{
			template: 2, metrics: [
				{ text: "Raw Value", value: 0 },
				// { text: "Usage", value: 1 },
			]
		},
		{
			template: 3, metrics: [
				{ text: "Period Average", value: 0 },
				{ text: "Running Average", value: 1 },
				{ text: "Live Value", value: 2 },
				{ text: "Max Value", value: 3 }
			]
		},
		{
			template: 4, metrics: [
				{ text: "Shortcut Destination", value: 0 }
			]
		},
		{
			template: 5, metrics: [
				{ text: "Watch List", value: 0 }
			]
		},
		{
			template: 6, metrics: [
				{ text: "Actions", value: 0 }
			]
		},
		{
			template: 7, metrics: [
				{ text: "Rules", value: 0 }
			]
		},
		{
			template: 8, metrics: [
				{ text: "Events", value: 0 }
			]
		},
		{
			template: 9, metrics: [
				{ text: "Low", value: 0 },
				{ text: "Normal", value: 1 },
				{ text: "High", value: 2 }
			]
		},
		{
			template: 10, metrics: [
				{ text: "Low", value: 0 },
				{ text: "Normal", value: 1 },
				{ text: "High", value: 2 }
			]
		},
		{
			template: 11, metrics: [
				{ text: "Webhooks", value: 0 }
			]
		},
	];

	/** panelInfo */
	public panelInfo: {
		id: string,
		template: string,
		templateID: number,
		metrics: number[],
		context: any,
		complete?: boolean,
		title?: string,
		isHierarchy?: boolean,
		datapoints?: DataPoint[],
		barDefault?: boolean
	}[] = [];

	/** dashboardName */
	public dashboardName: string = "";

	/** existingDashboards */
	public existingDashboards: OrganisationDashboard[] = [];

	/** onLoadedDashboard */
	public onLoadedDashboard: boolean = false;

	/** loadedDashboard */
	public loadedDashboard: OrganisationDashboard | undefined;

	/** editing */
	public editing: boolean = false;

	/** deleting */
	public deleting: boolean = false;

	/** defaultDashboard */
	public defaultDashboard: boolean = false;

	/** adding */
	public adding: boolean = false;

	/** preDragPositions */
	public preDragPositions: { row: number, col: number, id: string }[] = [];

	/** refreshGauges */
	public refreshGauges: boolean = false;

	/** refreshCharts */
	public refreshCharts: boolean = false;

	/** panelViews */
	private panelViews = new Map<string, EmbeddedViewRef<any>>();

	/** tabs */
	@ViewChild("tabs") tabs: TabComponent;

	/** allowEditing */
	public allowEditing: boolean = true;

	/** selectedPanels */
	public selectedPanels: { element: HTMLElement, dbPanel: PanelModel, panel: any }[] = [];

	/** isSelecting */
	public isSelecting: boolean = false;

	/** copiedPanels */
	public copiedPanels: { element: HTMLElement, dbPanel: PanelModel, panel: any }[] = [];
	/** preResizingDimensions */
	public preResizingDimensions: { id: string, sizeX: number, sizeY: number }[] = [];

	/** preResizingPositions */
	public preResizingPositions: { id: string, col: number, row: number }[] = [];

	/** defaultColorHexValue */
	public defaultColorHexValues: { name: string, hex: string }[] = [
		{ name: "green", hex: "#008000" },
		{ name: "orange", hex: "#FFA500" },
		{ name: "red", hex: "#FF0000" }
	];

	/** shortMonths */
	private shortMonths: string[] = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
	];

	/**
	 * Default Constructor
	 * @param auth auth  
	 * @param organisationDataService organisationDataService  
	 * @param globalFunctions globalFunctions 
	 * @param router router 
	 * @param route route 
	 * @param siteSubscriptions siteSubscriptions  
	 * @param authenticationService authenticationService 
	 * @param dialog dialog 
	 * @param document document 
	 * @param vcr vcr
	 */
	constructor(
		public auth: AuthService,
		public organisationDataService: OrganisationDataService,
		public globalFunctions: GlobalFunctions,
		public router: Router,
		private route: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private authenticationService: AuthenticationService,
		public dialog: MatDialog,
		@Inject(DOCUMENT) public document: Document,
		private vcr: ViewContainerRef
	) {
		/** Get required subscriptions */
		combineLatest([
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisation$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.queryParamMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([currentUser, selectedAccount, selectedOrganisationUID, paramMap, queryParamMap]) =>
				!!currentUser && !!selectedAccount && !!selectedOrganisationUID && paramMap.has('organisation'))
		).subscribe({
			next: ([currentUser, selectedAccount, selectedOrganisation, paramMap, queryParamMap]) => {
				this.thisUser = currentUser;

				this.selectedAccount = selectedAccount;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.accountGUID = this.selectedAccount.GUID;

				this.organisation = <IOrganisation>selectedOrganisation;
				this.organisationUID = this.organisation.Uid;

				if (queryParamMap.has("dashboard")) {
					this.getDashboards(parseInt(queryParamMap.get("dashboard") ?? ""));
				} else {
					this.getDashboards();
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * ngAfterViewInit
	 */
	ngAfterViewInit(): void {
		this.resize();

		this.templates = [
			this.textPanelTemplate,
			this.gaugePanelTemplate,
			this.chartPanelTemplate,
			this.listPanelTemplate,
			this.shortcutPanelTemplate,
			this.linearGaugePanelTemplate,
			this.annotatedGaugePanelTemplate,
			this.pieChartPanelTemplate
		];

		this.hierarchyTemplates = [
			this.textPanelTemplate,
			this.gaugePanelTemplate,
			this.chartPanelTemplate,
			this.listPanelTemplate,
			this.shortcutPanelTemplate,
			this.watchListPanelTemplate,
			this.actionsPanelTemplate,
			this.rulesPanelTemplate,
			this.eventsPanelTemplate,
			this.linearGaugePanelTemplate,
			this.annotatedGaugePanelTemplate,
			this.webhooksPanelTemplate
		];

		this.addFloorPanel();

		if (this.tabs) {
			//TODO: update tab component 
			// this.tabs.swipeMode
		}
	}

	/**
	 * addNewPanel
	 */
	addNewPanel(row: number = 0, col: number = 0) {
		if (this.dashboard) {
			let temp = document.createElement('div');
			temp.appendChild(this.getHTMLElement(this.defaultPanelTemplate, this.panelIDIndex.toString()));
			let newPanel: PanelModel = {
				row: row,
				col: col,
				sizeX: 2,
				sizeY: 2,
				content: `<div id=${this.panelIDIndex.toString()} class="panel-host alignColumn" style="width: 100%; height: 100%;">
				</div>`,
				id: this.panelIDIndex.toString()
			};
			this.panelIDIndex++;
			setTimeout(() => {
				if (this.checkPanelCanBeAdded(newPanel)) {
					this.dashboard.addPanel(newPanel);
					setTimeout(() => {
						this.renderTemplateIntoPanel(newPanel.id!, this.defaultPanelTemplate, {});
					}, 1);
					// this.correctPanelPositions();
				}
			}, 0);
		}
	}

	/**
	 * getHTMLElement
	 * @param template template  
	 * @returns 
	 */
	getHTMLElement(template: TemplateRef<any>, id?: string, data?: any): HTMLElement {
		let view;
		if (data) {
			view = template.createEmbeddedView({ data });
		} else {
			view = template.createEmbeddedView({});
		}
		view.detectChanges();
		let element = view.rootNodes[0] as HTMLElement;
		if (id) {
			element.id = id;
		}
		return element;
	}

	/**
	 * correctPanelPositions
	 */
	correctPanelPositions() {
		if (this.dashboard) {
			this.dashboard.panels.forEach((x: PanelModel) => {
				if (x.col) {
					if (x.col < 0) { x.col = 0; }
					if (x.col > this.maxColumns) { x.col = this.maxColumns };
				}
				if (x.row) {
					if (x.row < 0) { x.row = 0; }
					if (x.row > this.maxRows) {
						x.row = this.maxRows
					};
				}
			});
		}
	}

	/**
	 * checkPanelCanBeAdded
	 * @param panel panel 
	 * @returns 
	 */
	checkPanelCanBeAdded(panel: PanelModel): boolean {
		// let columnOK: boolean = false;
		// let rowOK: boolean = false;
		// if (panel.col != undefined && panel.sizeX) {
		// 	if (panel.col >= 0 && (panel.col + panel.sizeX) < this.maxColumns) {
		// 		columnOK = true;
		// 	}
		// }
		// if (panel.row != undefined && panel.sizeY) {
		// 	if (panel.row >= 0 && (panel.row + panel.sizeY) < this.maxRows) {
		// 		rowOK = true;
		// 	}
		// }
		// return (columnOK && rowOK);
		return true;
	}

	/**
	 * respondToDrag
	 * @param dragEvent dragEvent  
	 */
	respondToDrag(dragEvent: TreeDashboardDrag, isHierarchyNode: boolean = false) {
		if (this.dashboard) {
			const panel: PanelModel | undefined = this.dashboard.panels.find((x: PanelModel) => x.id == dragEvent.panelID);
			if (panel) {
				if (!isHierarchyNode) {
					let data: DashboardPanelTemplateInputData = {
						accountGUID: this.accountGUID,
						organisationUID: this.organisationUID,
						templates: this.templateTypes,
						templateMetrics: this.templateMetrics,
						node: dragEvent.draggedNode,
						isHierarchyNode: false,
						htmlTemplates: this.templates
					};
					let existingInfo = this.panelInfo.find((x) => x.id == panel.id);
					if (existingInfo) {
						const metricData = {
							metrics: data.templateMetrics[existingInfo.templateID].metrics,
							metricChoices: existingInfo.metrics
						};
						let datapoints: DataPoint[] = [];
						this.flattenNodes([dragEvent.draggedNode], true).forEach((x) => datapoints.push(...x.Datapoints ?? []));
						if (existingInfo.template == "List") {
							if (existingInfo.context.datapoints) {
								datapoints.push(...existingInfo.context.datapoints);
							}
						}
						const contextData: {
							nodeData: any,
							metricData: any,
							datapoints: DataPoint[],
							title: string,
							id: string,
							ranges?: { min: number[], max: number[], color: string[], name: string[], createsBorder: boolean[] }[]
						} = {
							nodeData: dragEvent.draggedNode,
							metricData: metricData,
							datapoints: datapoints,
							title: dragEvent.draggedNode.Name,
							id: dragEvent.panelID
						};
						if ([5, 6].includes(existingInfo.templateID)) {
							contextData.ranges = existingInfo.context.ranges;
						}
						this.renderTemplateIntoPanel(dragEvent.panelID, this.templates[existingInfo.templateID], contextData, true, true);
						setTimeout(() => {
							this.resize();
						}, 1);
						this.logPanelInfo(dragEvent.panelID,
							this.templateTypes[existingInfo.templateID].text,
							this.templateTypes[existingInfo.templateID].value,
							metricData.metricChoices,
							contextData,
							false,
							true
						);
					} else {
						const dialogRef = this.dialog.open(DashboardPanelTemplateComponent, {
							data: data
						});

						dialogRef.afterClosed().pipe(
							filter(result => !!result)
						).subscribe(result => {
							let metricChoices: number[] = [];
							for (let i: number = 1; i < result["numberOfMetrics"] + 1; i++) {
								if (result[`metric${i}`] != undefined) {
									metricChoices.push(result[`metric${i}`]);
								}
							}
							const metricData = {
								metrics: data.templateMetrics[result["template"]].metrics,
								metricChoices: metricChoices
							};
							let datapoints: DataPoint[] = [];
							if ([1, 5, 6].includes(result.template)) {
								datapoints = result.datapoints;
							} else {
								this.flattenNodes([dragEvent.draggedNode]).forEach((x) => datapoints.push(...x.Datapoints ?? []));
							}
							const contextData: {
								nodeData: any,
								metricData: any,
								datapoints: DataPoint[],
								title: string,
								id: string,
								ranges?: { min: number[], max: number[], color: string[], name: string[], createsBorder: boolean[] }[],
								totalise?: boolean,
								seriesColours?: any,
								isHierarchy?: boolean
							} = {
								nodeData: dragEvent.draggedNode,
								metricData: metricData,
								datapoints: datapoints,
								title: dragEvent.draggedNode.Name,
								id: dragEvent.panelID,
								seriesColours: result.seriesColours,
								isHierarchy: false
							};
							if ([1, 5, 6].includes(result.template)) {
								let ranges = [
									{
										min: result.range1Min, max: result.range1Max, color: result.range1Colour,
										name: result.range1Name, createsBorder: result.range1Border
									},
									{
										min: result.range2Min, max: result.range2Max, color: result.range2Colour,
										name: result.range2Name, createsBorder: result.range2Border
									},
									{
										min: result.range3Min, max: result.range3Max, color: result.range3Colour,
										name: result.range3Name, createsBorder: result.range3Border
									},
								];
								contextData.ranges = ranges;
								contextData.totalise = result.totalise ?? false;
							}
							this.renderTemplateIntoPanel(dragEvent.panelID, this.templates[result["template"]], contextData, true, true);
							setTimeout(() => {
								this.resize();
							}, 1);
							this.logPanelInfo(dragEvent.panelID,
								this.templateTypes[result["template"]].text,
								this.templateTypes[result["template"]].value,
								metricChoices,
								contextData
							);
						});
					}
				} else {
					let data: DashboardPanelTemplateInputData = {
						accountGUID: this.accountGUID,
						organisationUID: this.organisationUID,
						templates: this.hierarchyTemplateTypes,
						templateMetrics: this.hierarchyTemplateMetrics,
						node: dragEvent.draggedNode,
						isHierarchyNode: true,
						htmlTemplates: this.hierarchyTemplates
					};
					let existingInfo = this.panelInfo.find((x) => x.id == panel.id);
					const dialogRef = this.dialog.open(DashboardPanelTemplateComponent, {
						data: data
					});

					dialogRef.afterClosed().pipe(
						filter(result => !!result)
					).subscribe(result => {
						let metricChoices: number[] = [];
						for (let i: number = 1; i < result["numberOfMetrics"] + 1; i++) {
							if (result[`metric${i}`] != undefined) {
								metricChoices.push(result[`metric${i}`]);
							}
						}
						const metricData = {
							metrics: data.templateMetrics[result["template"]].metrics,
							metricChoices: metricChoices
						};
						let contextData: {
							nodeData: any,
							metricData: any,
							title: string,
							id: string,
							datapoints?: DataPoint[],
							assets?: LevelAsset[],
							ranges?: { min: number[], max: number[], color: string[], name: string[], createsBorder: boolean[] }[]
							totalise?: boolean,
							webhooks?: IWebhookIntegration[],
							seriesColours?: any,
							isHierarchy?: boolean
						} | undefined = {
							nodeData: dragEvent.draggedNode,
							metricData: metricData,
							title: dragEvent.draggedNode.Name,
							id: dragEvent.panelID,
							datapoints: result.datapoints,
							seriesColours: result.seriesColours,
							isHierarchy: true
						}
						if ([1, 9, 10].includes(result.template)) {
							let ranges = [
								{
									min: result.range1Min, max: result.range1Max, color: result.range1Colour,
									name: result.range1Name, createsBorder: result.range1Border
								},
								{
									min: result.range2Min, max: result.range2Max, color: result.range2Colour,
									name: result.range2Name, createsBorder: result.range2Border
								},
								{
									min: result.range3Min, max: result.range3Max, color: result.range3Colour,
									name: result.range3Name, createsBorder: result.range3Border
								},
							];
							contextData.ranges = ranges;
							contextData.totalise = result.totalise ?? false;
						} else if ([5, 6, 7, 8].includes(result.template)) {
							const assets = this.getAssetsFromHierarchyTreeNode(dragEvent.draggedNode);
							contextData.assets = assets;
						} else if (result.template == 11) {
							contextData.webhooks = result.webhooks;
						}
						this.renderTemplateIntoPanel(dragEvent.panelID, this.hierarchyTemplates[result["template"]], contextData, true, true, true);
						setTimeout(() => {
							this.resize();
						}, 1);
						this.logPanelInfo(dragEvent.panelID,
							this.hierarchyTemplateTypes[result["template"]].text,
							this.hierarchyTemplateTypes[result["template"]].value,
							metricChoices,
							contextData,
							false,
							existingInfo ? true : false,
							true
						);
					});

				}
			}
		}
	}

	/**
	 * createGauge
	 * @param data data  
	 * @returns 
	 */
	createGauge(data: any): HTMLElement {
		this.vcr.clear();

		const view = this.vcr.createEmbeddedView(
			this.gaugePanelTemplate,
			{ data }
		);

		view.detectChanges();
		return view.rootNodes[0] as HTMLElement;
	}

	/** 
	 * renderTemplateIntoPanel 
	 */
	renderTemplateIntoPanel(panelId: string, template: TemplateRef<any>, data: any, removeDefault: boolean = false, clear: boolean = false, isHierarchy: boolean = false) {
		const host = document.getElementById(panelId)?.getElementsByClassName("panel-host")[0];
		if (!host) return;
		if (!host.classList.contains("scaling-text-height")) {
			host.classList.add("scaling-text-height");
		}

		if (isHierarchy && Object.hasOwn(data, "nodeData") && data.nodeData.Uid == this.organisation.Uid) {
			data.nodeData = undefined;
			data.assets = [];
		}

		const view = this.vcr.createEmbeddedView(template, { data });
		this.panelViews.set(panelId, view);
		view.detectChanges();

		if (removeDefault) {
			const defaultEle = host.getElementsByClassName("default-panel")[0];
			if (defaultEle) {
				host.removeChild(defaultEle);
			}
		}

		if (clear) {
			host.childNodes.forEach((x) => host.removeChild(x));
		}
		host.appendChild(view.rootNodes[0]);
	}

	/**
	 * resizeFromDrag
	 * @param args args 
	 */
	resizeFromDrag(args: any) {
		let invalid: boolean = false;
		this.dashboard.panels.forEach((x: PanelModel) => {
			if (x.id != "__floor__") {
				if ((x.row && x.sizeY) && (x.row + x.sizeY) > this.maxRows) {
					invalid = true;
				}
			} else {
				if (x.row != this.maxRows) {
					invalid = true;
				}
			}
		});
		if (invalid) {
			args.cancel = true;
			setTimeout(() => {
				this.preResizingDimensions.forEach((dimension) => {
					this.dashboard.resizePanel(dimension.id, dimension.sizeX, dimension.sizeY);
				});
				this.preResizingDimensions = [];
				this.preResizingPositions.forEach((position) => {
					this.dashboard.movePanel(position.id, position.row, position.col);
				});
				this.preResizingPositions = [];
				let floorPanel = this.dashboard.panels.find((x: PanelModel) => x.id == "__floor__");
				if (floorPanel) {
					this.dashboard.movePanel("__floor__", this.maxRows, 0);
				}
			}, 1);
		} else {
			this.preResizingDimensions = [];
			this.preResizingPositions = [];
			this.resize();
		}
	}

	/**
	 * resize
	 */
	resize() {
		this.resizeGauges();
		this.resizeCharts();
		// this.refreshLists();
	}

	/**
	 * resizeGauges
	 */
	resizeGauges() {
		this.refreshGauges = true;
		setTimeout(() => {
			this.refreshGauges = false;
		}, 0);
		// this.allGauges.forEach((x: CircularGaugeComponent) => {
		// 	const height: number = x.element.parentElement?.parentElement?.offsetHeight ?? 0;
		// 	x.height = `${height}px`;
		// 	x.refresh();
		// });
	}

	/**
	 * resizeCharts
	 */
	resizeCharts() {
		this.refreshCharts = true;
		setTimeout(() => {
			this.refreshCharts = false;
		}, 0);
		// this.allCharts.forEach((x: ChartComponent) => {
		// 	const parent = x.element.parentElement?.parentElement;
		// 	const height: number = parent?.offsetHeight ?? 0;
		// 	const width: number = parent?.offsetWidth ?? 0;
		// 	x.height = `${height}px`;
		// 	x.width = `${width}px`;
		// 	x.refresh();
		// });
	}

	/**
	 * refreshLists
	 */
	refreshLists() {
		this.allLists.forEach((x: ListViewComponent) => {
			const parent = x.element.parentElement?.parentElement;
			const height: number = parent?.offsetHeight ?? 0;
			// x.element.style.maxHeight = `${height * 0.6}px`;
			// x.element.style.overflowY = 'auto';
			x.dataBind();
			x.refresh();
		});
	}

	/**
	 * saveDashboard
	 */
	saveDashboard() {
		let panelsWithInfo: { panel: PanelModel, info: any }[] = [];
		let panels: PanelModel[] = this.dashboard.serialize();
		panels.forEach((x: PanelModel) => {
			const info = this.panelInfo.find((y) => y.id == x.id);
			if (info && info.complete) {
				panelsWithInfo.push({ panel: x, info: info });
			}
		});
		if (!this.onLoadedDashboard) {
			this.postDashboard(JSON.stringify(panelsWithInfo));
		} else {
			if (this.loadedDashboard) {
				this.updateDashboard(this.loadedDashboard.OrganisationDashboardID, this.loadedDashboard.DashboardName, JSON.stringify(panelsWithInfo));
			}
		}
	}

	/**
	 * logPanelInfo
	 * @param id id 
	 * @param template template  
	 * @param templateID templateID 
	 * @param metrics metrics 
	 */
	logPanelInfo(id: string, template: string, templateID: number, metrics: number[], context: any, notComplete: boolean = false, replace: boolean = false, isHierarchy: boolean = false) {
		if (!replace) {
			this.panelInfo.push(
				{
					id: id,
					template: template,
					templateID: templateID,
					metrics: metrics,
					context: context,
					complete: notComplete ? false : true,
					isHierarchy: isHierarchy
				}
			);
		} else {
			let existingInfoIndex = this.panelInfo.findIndex((x) => x.id == id);
			if (existingInfoIndex > -1) {
				this.panelInfo[existingInfoIndex] =
				{
					id: id,
					template: template,
					templateID: templateID,
					metrics: metrics,
					context: context,
					complete: notComplete ? false : true,
					isHierarchy: isHierarchy
				}
			}
		}
	}

	/**
	 * nameChange
	 * @param event event  
	 */
	nameChange(event: any) {
		this.dashboardName = event.target.value;
	}

	/**
	 * checkDashboardValid
	 * @returns boolean
	 */
	checkDashboardValid(): boolean {
		return (this.dashboardName.length > 0)
			&& (this.panelInfo.length > 0)
			&& (!this.deleting)
			&& (this.allowEditing)
			&& (!this.editing)
			&& (!this.adding);
	}

	/**
	 * postDashboard
	 * @param config config  
	 */
	postDashboard(config: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// set utility dashboard to true if the account has the utilities feature and has used at least 1 cumulative datapoint
		let utilityDashboard: boolean = false;
		if (this.checkFeature(AccountFeatures.Energy_Monitoring)) {
			utilityDashboard = this.flattenNodes(JSON.parse(config)
				.map((x: any) => x.info.context.nodeData))
				.map((x: OrganisationDataModelNode) => x.Datapoints ?? [])
				.some((x: DataPoint[]) => x.some((y: DataPoint) => y.IsCumulative));
		}

		this.organisationDataService.postOrganisationDashboard$(this.accountGUID, this.organisationUID, this.dashboardName, config, this.defaultDashboard, utilityDashboard).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.getDashboards(this.dashboardName);
			})
		).subscribe({
			next: (response) => {
				this.globalFunctions.DisplaySnackBar({
					message: response["Result"] == "Error" ? response["Error"] : response["Result"].replace("OK -", ""),
					duration: 2000,
					action: ""
				});
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.organisationDataService.postOrganisationDashboard$:", error);
				}
			},
		});
	}

	/**
	 * getDashboards
	 */
	getDashboards(loadAfterGet?: string | number) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.organisationDataService.getOrganisationDashboard$(this.accountGUID, this.organisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((dashboards) => !!dashboards),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (dashboards) => {
				this.existingDashboards = dashboards.map((x: any) => ({
					...x,
					DashboardConfig: JSON.parse(x.DashboardConfig)
				}));
				if (loadAfterGet && typeof (loadAfterGet) == "string") {
					const dash = this.existingDashboards.find((x: OrganisationDashboard) => x.DashboardName == loadAfterGet);
					if (dash) {
						this.loadDashboard(dash);
					}
				} else if (loadAfterGet && typeof (loadAfterGet) == "number") {
					const dash = this.existingDashboards.find((x: OrganisationDashboard) => x.OrganisationDashboardID == loadAfterGet);
					if (dash) {
						this.loadDashboard(dash);
					}
				}
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.organisationDataService.getOrganisationDashboard$:", error);
				}
			},
		});
	}

	/**
	 * resetDashboard
	 */
	resetDashboard() {
		this.panelViews.forEach(view => view.destroy());
		this.panelViews.clear();
		this.panelInfo = [];
		this.dashboard.panels = [];
		this.dashboardName = "";
		this.loadedDashboard = undefined;
		this.panelIDIndex = 0;
		this.editing = false;
		this.deleting = false;
		this.isSelecting = false;
		this.defaultDashboard = false;
		this.addFloorPanel();
	}

	/**
	 * loadDashboard
	 * @param dashboard dashboard  
	 */
	loadDashboard(dashboard: OrganisationDashboard) {
		this.resetDashboard();
		this.onLoadedDashboard = true;
		this.allowEditing = !dashboard.Locked;
		this.loadedDashboard = dashboard;
		this.dashboardName = dashboard.DashboardName;
		this.dashboard.panels = dashboard.DashboardConfig
			.map((x) => ({ ...x.panel, content: `<div id=${x.panel.id} class="panel-host" style="width: 100%; height: 100%;"></div>` }));
		this.panelInfo = dashboard.DashboardConfig.map((x) => x.info);
		this.panelInfo.forEach((x) => {
			if (parseInt(x.id) > this.panelIDIndex) {
				this.panelIDIndex = parseInt(x.id);
			}
		});
		this.panelIDIndex++;
		this.defaultDashboard = dashboard.DefaultDashboard;
		setTimeout(() => {
			this.panelInfo.forEach((x) => {
				this.renderTemplateIntoPanel(x.id, x.isHierarchy ? this.hierarchyTemplates[x.templateID] : this.templates[x.templateID], { ...x.context, barDefault: x.barDefault });
				setTimeout(() => {
					this.resize();
				}, 1);
			});
		}, 1);
	}

	/**
	 * newDashboard
	 */
	newDashboard() {
		if (this.panelInfo.length > 0) {
			this.globalFunctions.UserConfirm$("Are you sure you want to create a new Dashboard? All unsaved changes will be lost.").subscribe(OKResult => {
				if (OKResult) {
					this.resetDashboard();
					this.onLoadedDashboard = false;
					this.allowEditing = true;
				}
			});
		} else if (this.panelInfo.length == 0) {
			this.resetDashboard();
			this.onLoadedDashboard = false;
		}
	}

	/**
	 * updateDashboard
	 * @param dashboardID dashboardID 
	 * @param name name 
	 * @param config config 
	 */
	updateDashboard(dashboardID: number, name: string, config: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.organisationDataService.patchOrganisationDashboard$(this.accountGUID, this.organisationUID, dashboardID, name, config, this.defaultDashboard).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.getDashboards();
			})
		).subscribe({
			next: (response) => {
				this.globalFunctions.DisplaySnackBar({
					message: response["Result"] == "Error" ? response["Error"] : response["Result"].replace("OK -", ""),
					duration: 2000,
					action: ""
				});
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.organisationDataService.patchOrganisationDashboard$:", error);
				}
			},
		});
	}

	/** 
	 * enablePanelEditing 
	 */
	enablePanelEditing() {
		if (this.isSelecting) {
			this.globalFunctions.UserConfirm$("Edit Selected Panels?").subscribe(OKResult => {
				if (OKResult) {
					const template = new Set(this.selectedPanels.map((x) => x.panel.template));
					if (template.size == 1) {
						const templateName: string = template.values().next().value;
						if (templateName == "Linear Gauge" || templateName == "Annotated Gauge") {
							const placeHolderPanel = this.selectedPanels[0].panel;
							const panelInfo = this.panelInfo.find((x) => x.id == placeHolderPanel.id);
							if (panelInfo) {
								const affectedPanels = this.selectedPanels.map((x) => x.panel);
								const panelData = panelInfo.context;
								this.respondToEditComponentRequest(panelData, panelInfo, affectedPanels);
							}
						}
					}
				}
			});
		} else {
			this.editing = !this.editing;
		}
	}

	/**
	 * enablePanelDeleting
	 */
	enablePanelDeleting() {
		if (this.isSelecting) {
			this.globalFunctions.UserConfirm$("Delete Selected Panels?").subscribe(OKResult => {
				if (OKResult) {
					this.selectedPanels.forEach((x: any) => {
						this.dashboard.removePanel(x.panel.id);
						this.panelInfo = this.panelInfo.filter((y) => y.id != x.panel!.id);
					});
				}
			});
		} else {
			this.deleting = !this.deleting;
		}
	}

	/**
	 * deletePanel
	 * @param event event  
	 */
	deletePanel(event: any) {
		const target: HTMLElement = event.target;
		let parent: HTMLElement | null = target;
		let panel: HTMLElement | undefined = undefined;
		while (parent) {
			if (parent.classList.contains("panel-host")) {
				panel = parent;
				parent = null;
				break;
			} else {
				parent = parent.parentElement;
			}
		}
		if (panel) {
			this.globalFunctions.UserConfirm$("Delete selected panel?").subscribe(OKResult => {
				if (OKResult) {
					this.dashboard.removePanel(panel.id);
					this.panelInfo = this.panelInfo.filter((x) => x.id != panel!.id);
				}
			});
		}
		this.deleting = false;
	}

	/**
	 * setAsDefault
	 */
	setAsDefault() {
		this.defaultDashboard = true;
	}

	/**
	 * deleteDashboard
	 * @param dashboard dashboard  
	 */
	deleteDashboard(dashboard: OrganisationDashboard) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.organisationDataService.deleteOrganisationDashboard$(this.accountGUID, this.organisationUID, dashboard.OrganisationDashboardID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.getDashboards();
				this.resetDashboard();
			})
		).subscribe({
			next: (response) => {
				this.globalFunctions.DisplaySnackBar({
					message: response["Result"] == "Error" ? response["Error"] : response["Result"].replace("OK -", ""),
					duration: 2000,
					action: ""
				});
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.organisationDataService.deleteOrganisationDashboard$:", error);
				}
			},
		});
	}

	/**
	 * deleteClicked
	 */
	deleteClicked() {
		if (this.loadedDashboard) {
			this.globalFunctions.UserConfirm$(`Delete ${this.loadedDashboard.DashboardName}?`).subscribe(async OKResult => {
				if (OKResult) {
					this.deleteDashboard(this.loadedDashboard!);
				}
			});
		}
	}

	/**
	 * checkFeature
	 * @param feature feature   
	 * @returns 
	 */
	checkFeature(feature: AccountFeatures): boolean {
		if (this.selectedAccount && this.selectedAccount.Features) {
			return this.selectedAccount.Features.some((x: any) => x.FeatureID == feature && x.Enabled);
		}
		return false;
	}

	/**
	 * flattenNodes
	 * @param nodes nodes  
	 * @returns 
	 */
	flattenNodes(nodes: OrganisationDataModelNode[], includeTopLevelNodes: boolean = false): OrganisationDataModelNode[] {
		let result: OrganisationDataModelNode[] = [];

		// Check if nodes is a valid array before proceeding
		if (!Array.isArray(nodes)) {
			return result;
		}

		if (includeTopLevelNodes) {
			result.push(...nodes);
		}

		// Iterate over each item in the input hierarchy
		for (const item of nodes) {
			if (!item || !item.SubChild) continue;
			if (item.SubChild && item.SubChild.length > 0) {
				result = result.concat(item.SubChild);
				result = result.concat(this.flattenNodes(item.SubChild));
			}
		}

		return result;
	}

	/**
	 * handleClick
	 * @param event event  
	 */
	handleClick(event: MouseEvent) {
		let element: HTMLElement = <HTMLElement>event.target;
		const row: number = parseInt(element.getAttribute("data-row") ?? '');
		const col: number = parseInt(element.getAttribute("data-col") ?? '');
		if (!Number.isNaN(row) && !Number.isNaN(col)) {
			if (this.adding) {
				this.addNewPanel(row, col);
				this.adding = false;
			}
		}
	}

	/**
	 * newPanelClick
	 * @param event event 
	 */
	newPanelClick(event: any) {
		const id: string = event.currentTarget.parentElement.parentElement.id;

		let data: DashboardPanelTemplateInputData = {
			accountGUID: this.accountGUID,
			organisationUID: this.organisationUID,
			templates: this.templateTypes,
			templateMetrics: this.templateMetrics,
			includeShortcut: true,
			dashboards: this.existingDashboards,
			htmlTemplates: this.templates
		};
		const dialogRef = this.dialog.open(DashboardPanelTemplateComponent, {
			data: data
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			let metricChoices: number[] = [];
			if (result.template != 4) {
				for (let i: number = 1; i < result["numberOfMetrics"] + 1; i++) {
					if (result[`metric${i}`] != undefined) {
						metricChoices.push(result[`metric${i}`]);
					}
				}
			} else {
				metricChoices = [result.metric1];
			}
			const metricData = {
				metrics: data.templateMetrics[result["template"]].metrics,
				metricChoices: metricChoices
			};
			const contextData: { nodeData: any, metricData: any } = {
				nodeData: result.template != 4 ? {} : this.existingDashboards.find((x: OrganisationDashboard) => x.OrganisationDashboardID == result.metric1),
				metricData: metricData
			};
			this.renderTemplateIntoPanel(id, this.templates[result["template"]], contextData, true);
			setTimeout(() => {
				this.resize();
			}, 1);
			this.logPanelInfo(id,
				this.templateTypes[result["template"]].text,
				this.templateTypes[result["template"]].value,
				metricChoices,
				contextData,
				result.template != 4 ? true : false
			);
		});
	}

	/**
	 * addFloorPanel
	 */
	addFloorPanel() {
		if (this.dashboard) {
			setTimeout(() => {
				this.dashboard.addPanel({
					id: '__floor__',
					row: this.maxRows,
					col: 0,
					sizeX: 10,
					sizeY: 1,
					enabled: false
				});
			}, 1);
			this.hideBottomGridRow();
		}
	}

	/**
	 * dragging
	 * @param event event  
	 */
	dragging(event: any) {
		let draggingElement: HTMLElement = event.element;
		let bottomPosition: number = parseInt(draggingElement.getAttribute("data-row") ?? '0') + parseInt(draggingElement.getAttribute("data-sizeY") ?? '0');
		if (bottomPosition >= this.maxRows) {
			let panel = this.dashboard.panels.find((x: PanelModel) => x.id == draggingElement.getAttribute("id"));
			if (panel) {
				panel.row = this.maxRows;
				setTimeout(() => {
					this.dashboard.refresh();
				}, 0);
			}
		}
	}

	/**
	 * dragStop
	 * @param event event  
	 */
	dragStop(args: any) {
		let invalid: boolean = false;
		this.dashboard.panels.forEach((x: PanelModel) => {
			if (x.id != "__floor__") {
				if ((x.row && x.sizeY) && (x.row + x.sizeY) > this.maxRows) {
					invalid = true;
				}
			} else {
				if (x.row != this.maxRows) {
					invalid = true;
				}
			}
		});
		if (invalid) {
			args.cancel = true;
			setTimeout(() => {
				let floorPanel = this.dashboard.panels.find((x: PanelModel) => x.id == "__floor__");
				if (floorPanel) {
					this.dashboard.movePanel("__floor__", this.maxRows, 0);
				}
			}, 1);
		}
		this.preDragPositions = [];
	}

	/**
	 * dragStart
	 * @param event event  
	 */
	dragStart(event: any) {
		this.preDragPositions = [];
		this.dashboard.panels.forEach((x: PanelModel) => {
			this.preDragPositions.push({
				row: x.row ?? 0,
				col: x.col ?? 0,
				id: x.id ?? ''
			});
		});
	}

	/**
	 * hideBottomGridRow
	 */
	hideBottomGridRow() {
		if (this.dashboard) {
			let dashboardElement: HTMLElement = this.dashboard.element;
			if (dashboardElement.childNodes.length > 0) {
				const grid = dashboardElement.getElementsByClassName("e-dashboard-gridline-table")[0];
				if (grid) {
					const body = grid.childNodes[0];
					if (body) {
						let nodesToHide = body.childNodes[10]?.childNodes;
						if (nodesToHide && nodesToHide.length > 0) {
							nodesToHide.forEach((x: ChildNode) => {
								if (x instanceof HTMLElement) {
									let xElement = x as HTMLElement;
									xElement.style.display = "none";
								}
							});
						}
					}
				}
			}
		}
	}

	/**
	 * panelTitleChange
	 * @param event event 
	 * @param data data 
	 */
	panelTitleChange(event: any, data: any) {
		const query = event.target.value;
		let info = this.panelInfo.find((x) => x.id == data.id);
		if (info) {
			info.context = { ...info.context, title: query };
			this.renderTemplateIntoPanel(info.id, info.isHierarchy ? this.hierarchyTemplates[info.templateID] : this.templates[info.templateID], info.context, true, true);
		}
	}

	/**
	 * panelTimeRangeChange - Git#122
	 * @param event event 
	 * @param data data 
	 */
	panelTimeRangeChange(datePicker: boolean, event: any, data: any) {
		let info = this.panelInfo.find((x) => x.id == data.id);
		if (info) {
			if (event.reset) {
				info.context.preset = undefined;
				info.context.range = undefined;
				info.context.duration = undefined;
				// this.renderTemplateIntoPanel(info.id, info.isHierarchy ? this.hierarchyTemplates[info.templateID] : this.templates[info.templateID], info.context, true, true);
			}
			else if (datePicker && event.event.target.classList.contains("e-list-item")) { //picked a preset
				info.context.range = undefined;
				info.context.duration = undefined;
				info.context.preset = event.event.target.innerText;
			} else if (datePicker) { //picked a range
				info.context.preset = undefined;
				info.context.duration = undefined;
				info.context.range = [event.value[0].getTime(), (event.value[1].getTime() + (24 * 60 * 60 * 1000))];
			} else if (!datePicker) { //picked a duration
				info.context.preset = undefined;
				info.context.range = undefined;
				info.context.duration = event;
			}
		}
	}

	/**
	 * getPanelCustomRange - Git#122
	 * @param data data 
	 */
	getPanelCustomRange(data: any): string {
		let result: string = "";
		let info = this.panelInfo.find((x) => x.id == data.id);
		if (info) {
			if (info.context.preset) {
				result = info.context.preset;
			}
			else if (info.context.range) {
				result = `${this.getShortDate(new Date(info.context.range[0]))} - ${this.getShortDate(new Date(info.context.range[1]))}`;
			}
			else if (info.context.duration) {
				const parts: string[] = info.context.duration.split(":");
				if (parts[0] != "00") {
					const num = parseInt(parts[0]);
					result = `${num} Day${num != 1 ? 's' : ''}`;
				}
				if (parts[1] != "00") {
					const num = parseInt(parts[1]);
					result = `${num} Hour${num != 1 ? 's' : ''}`;
				}
			}
		}
		return result;
	}

	/**
	 * getPanelBackgroundImageName
	 */
	getPanelBackgroundImageName(data: any): string {
		let result: string = "";
		let info = this.panelInfo.find((x) => x.id == data.id);
		if (info) {
			result = info.context.backgroundFileName ?? "";
		}
		return result;
	}

	/**
	 * getPanelTextColour
	 */
	getPanelTextColour(data: any): string {
		let result: string = "";
		let info = this.panelInfo.find((x) => x.id == data.id);
		if (info) {
			result = info.context.textColour ?? "";
		}
		return result;
	}

	/**
	 * getPanelInfo
	 * @param data data 
	 */
	getPanelInfo(data: any): any {
		return this.panelInfo.find((x) => x.id == data.id);
	}

	/**
	 * getAssetsFromHierarchyTreeNode
	 * @param treeNode treeNode 
	 */
	getAssetsFromHierarchyTreeNode(treeNode: OrganisationsHierarchy): LevelAsset[] {
		let result: LevelAsset[] = [];
		const flattenedLocations = this.flattenSubLocations([treeNode], true);
		flattenedLocations.forEach((x) => {
			if (this.isParsableAsNumber(x.Uid)) {
				result.push(x)
			}
		})
		return result;
	}

	/**
	 * flattenSubLocations
	 * @param hierarchy hierarchy 
	 * @returns 
	 */
	flattenSubLocations(hierarchy: OrganisationsHierarchy[], includeTopLevel: boolean = false): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		if (includeTopLevel) {
			result.push(...hierarchy);
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.SubChild && item.SubChild.length > 0) {
				result = result.concat(item.SubChild);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenSubLocations(item.SubChild));
		}

		return result;
	}

	/**
	 * Check if string can be parsed as int
	 * @param str
	 * @returns
	 */
	isParsableAsNumber(str: any): boolean {
		const parsed = Number(str);
		return !isNaN(parsed);
	}

	/**
	 * setDefaultSeries
	 * @param event event 
	 */
	setDefaultSeries(event: any) {
		const id: string = event.panelID;
		const barSeries: boolean = event.barSeries;
		let info = this.panelInfo.find((x) => x.id == id);
		if (info) {
			info.barDefault = barSeries;
		}
	}

	/**
	 * editPanel
	 * @param event event 
	 */
	editPanel(event: any) {
		const target: HTMLElement = event.target;
		let parent: HTMLElement | null = target;
		let panel: HTMLElement | undefined = undefined;
		while (parent) {
			if (parent.classList.contains("panel-host")) {
				panel = parent;
				parent = null;
				break;
			} else {
				parent = parent.parentElement;
			}
		}
		if (panel != undefined) {
			const panelInfo = this.panelInfo.find((x) => x.id == panel!.id);
			if (panelInfo) {
				const panelData = panelInfo.context;
				this.respondToEditComponentRequest(panelData, panelInfo);
			}
		}
	}

	/**
	 * respondToEditComponentRequest
	 * @param event event 
	 */
	respondToEditComponentRequest(panelData: any, panelInfo: any, affectedPanels?: any[]) {
		const template = panelInfo.isHierarchy
			? this.hierarchyTemplates[panelInfo.templateID]
			: this.templates[panelInfo.templateID];
		const templateMetric = panelInfo.isHierarchy
			? this.hierarchyTemplateMetrics.find((x) => x.template == panelInfo.templateID)
			: this.templateMetrics.find((x) => x.template == panelInfo.templateID);
		const data: EditDashboardPanelDialogData = {
			panelData: panelData,
			panelInfo: panelInfo,
			template: template,
			templateInfo: templateMetric
		}

		const dialogRef = this.dialog.open(EditDashboardPanelComponent, {
			data: data
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log(result);
			this.editing = false;
			this.renderTemplateIntoPanel(panelInfo.id, panelInfo.isHierarchy ? this.hierarchyTemplates[panelInfo.templateID] : this.templates[panelInfo.templateID], panelInfo.context, true, true);
			if (affectedPanels && affectedPanels.length > 0) {
				const newRanges = result.panelData.ranges;
				affectedPanels.forEach((panel) => {
					panel.context.ranges = newRanges;
				});
				this.selectedPanels.forEach((x) => x.element.classList.remove('selected'));
				this.selectedPanels = [];
				this.isSelecting = false;
			}
		});
	}

	/**
	 * startResize
	 * @param event event  
	 */
	startResize(event: any) {
		const element = event.element as HTMLElement;
		const sizeX = element.getAttribute("data-sizex");
		const sizeY = element.getAttribute("data-sizey");
		const id = element.id;
		if (id != undefined && sizeX != undefined && sizeY != undefined && event.isInteracted) {
			this.preResizingDimensions.push({
				id: id,
				sizeX: parseInt(sizeX),
				sizeY: parseInt(sizeY)
			});
			this.dashboard.panels.forEach((panel: PanelModel) => {
				if (panel.id != "__floor__") {
					this.preResizingPositions.push({
						id: panel.id!,
						col: panel.col!,
						row: panel.row!
					});
				}
			});
		} else {
			event.cancel = true;
		}
	}

	/**
	 * toggleSelection
	 */
	toggleSelection() {
		this.isSelecting = !this.isSelecting;
		if (!this.isSelecting) {
			this.selectedPanels.map((x) => x.element).forEach((x: HTMLElement) => x.classList.remove('selected'));
			this.selectedPanels = [];
		}
	}

	/**
	 * selectPanel
	 * @param event event 
	 */
	selectPanel(event: any) {
		const target: HTMLElement = event.target;
		let parent: HTMLElement | null = target;
		let panel: HTMLElement | undefined = undefined;
		while (parent) {
			if (parent.classList.contains("panel-host")) {
				panel = parent;
				parent = null;
				break;
			} else {
				parent = parent.parentElement;
			}
		}
		if (panel != undefined) {
			const panelInfo = this.panelInfo.find((x) => x.id == panel!.id);
			if (panelInfo) {
				const dbPanel = this.dashboard.panels.find((x: PanelModel) => x.id == panelInfo.id);
				if (dbPanel) {
					if (this.selectedPanels.some((x: any) => x.panel.id == panelInfo.id)) {
						this.selectedPanels = this.selectedPanels.filter((x: any) => x.panel.id != panelInfo.id);
						panel.classList.remove('selected');
					} else {
						this.selectedPanels.push({ element: panel, dbPanel: dbPanel, panel: panelInfo });
						panel.classList.add('selected');
					}
				}
			}
		}
	}

	/**
	 * copyPanels
	 */
	copyPanels() {
		// this.copiedPanels = JSON.parse(JSON.stringify(this.selectedPanels));
		// this.copiedPanels = [ ...this.selectedPanels ];
		Object.assign(this.copiedPanels, this.selectedPanels);
		this.copiedPanels = [...this.selectedPanels];
		this.globalFunctions.DisplaySnackBar({
			message: `${this.selectedPanels.length} ${this.selectedPanels.length > 1 ? 'Panels' : 'Panel'} Copied`,
			duration: 2000,
			action: ""
		});
		this.toggleSelection();
	}

	/**
	 * pastePanels
	 */
	pastePanels() {
		let numFailedPastes: number = 0;
		this.copiedPanels.forEach((x) => {
			if (this.checkCanPastePanel(x.dbPanel)) {
				let id = x.panel.id;
				while (this.dashboard.panels.some((y: PanelModel) => y.id == id)) {
					id = `100${id}`;
					x.dbPanel.id = id;
					x.element.id = id;
					x.panel.id = id;
				}
				this.dashboard.addPanel(x.dbPanel);
				this.panelInfo.push(x.panel);
				this.renderTemplateIntoPanel(
					x.panel.id,
					x.panel.isHierarchy ? this.hierarchyTemplates[x.panel.templateID] : this.templates[x.panel.templateID],
					x.panel.context,
					true,
					true
				);
			} else {
				numFailedPastes++;
			}
		});
		if (numFailedPastes > 0) {
			this.globalFunctions.DisplaySnackBar({
				message: `${numFailedPastes} Panel${numFailedPastes != 1 ? 's' : ''} Did not have Room to Paste`,
				duration: 2000,
				action: ""
			});
		}
		this.copiedPanels = [];
	}

	/**
	 * checkCanPastePanel
	 * @param panel panel 
	 * @returns 
	 */
	checkCanPastePanel(panel: PanelModel): boolean {
		let result: boolean = true;
		const targetSquare: PanelPosition = this.getPanelPosition(panel);
		const targetArr: number[][] = this.convertPanelPositionToArray(targetSquare);
		const existingPositions: PanelPosition[] = this.dashboard.panels.map((p: PanelModel) => this.getPanelPosition(p));
		const existingArr: number[][][] = existingPositions.map((p: PanelPosition) => this.convertPanelPositionToArray(p));
		existingArr.forEach((panelArr: number[][]) => {
			panelArr.forEach((pos: number[]) => {
				if (targetArr.some((x: number[]) => x[0] == pos[0] && x[1] == pos[1])) {
					result = false;
				}
			});
		});
		return result;
	}

	/**
	 * getPanelPosition
	 * @param panel panel 
	 * @returns 
	 */
	getPanelPosition(panel: PanelModel): PanelPosition {
		return {
			topLeft: [panel.col!, panel.row!],
			topRight: [(panel.col! + panel.sizeX!), panel.row!],
			bottomLeft: [panel.col!, (panel.row! + panel.sizeY!)],
			bottomRight: [(panel.col! + panel.sizeX!), (panel.row! + panel.sizeY!)]
		};
	}

	/**
	 * convertPanelPositionToArray
	 * @param pos pos 
	 */
	convertPanelPositionToArray(pos: PanelPosition): number[][] {
		let result: number[][] = [];
		for (let row: number = pos.topLeft[1]; row < pos.bottomLeft[1]; row++) {
			for (let column: number = pos.topLeft[0]; column < pos.topRight[0]; column++) {
				result.push([column, row]);
			}
		}
		return result;
	}

	/**
	 * respondToBorderChange
	 * @param div div 
	 * @param colour colour 
	 */
	respondToBorderChange(div: HTMLElement, colour: string) {
		if (colour == "") {
			div.style.border = "";
			div.style.backgroundColor = "inherit";
		} else {
			div.style.border = `3px solid ${colour}`;
			div.style.borderRadius = 'var(--default-dashboard-border-radius)';
			let hex: string = "";
			if (!colour.includes("#")) {
				hex = this.getDefaultColorHexValue(colour);
			} else {
				hex = colour;
			}
			let rgb = this.convertHexToRGB(hex);
			div.style.backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25)`;
		}
	}

	/**
	 * convertHexToRGB
	 * @param hex hex 
	 */
	convertHexToRGB(hex: string): { r: number, g: number, b: number } {
		var bigint = parseInt(hex.slice(1, 7), 16);
		var r = (bigint >> 16) & 255;
		var g = (bigint >> 8) & 255;
		var b = bigint & 255;

		return { r: r, g: g, b: b };
	}

	/**
	 * getDefaultColorHexValue
	 * @param color color 
	 */
	getDefaultColorHexValue(color: string): string {
		let result: string = "";
		const _hex = this.defaultColorHexValues.find((x) => x.name == color)?.hex;
		if (_hex) result = _hex;
		return result;
	}

	/** getShortDate */
	getShortDate(date: Date): string {
		let result: string = "";
		result += date.getDate();
		result +=
			" " +
			this.shortMonths[date.getMonth()] +
			" " +
			date.getHours().toString().padStart(2, "0") +
			":" +
			date.getMinutes().toString().padStart(2, "0");

		return result;
	}

	/**
	 * panelBackgroundImageChange
	 * @param event event 
	 * @param data data 
	 */
	async panelBackgroundImageChange(file: File, data: any) {
		let info = this.panelInfo.find((x) => x.id == data.id);
		if (info) {
			const b64 = await this.toBase64(file);
			info.context.backgroundImage = b64;
			info.context.backgroundFileName = file.name;
			const context = { ...info.context, barDefault: info.barDefault };
			this.renderTemplateIntoPanel(info.id, info.isHierarchy ? this.hierarchyTemplates[info.templateID] : this.templates[info.templateID], context, true, true);
		}
	}

	/**
	 * panelBackgroundImageDelete
	 * @param data data 
	 */
	panelBackgroundImageDelete(data: any) {
		let info = this.panelInfo.find((x) => x.id == data.id);
		if (info) {
			info.context.backgroundImage = undefined;
			info.context.backgroundFileName = undefined;
			const context = { ...info.context, barDefault: info.barDefault };
			this.renderTemplateIntoPanel(info.id, info.isHierarchy ? this.hierarchyTemplates[info.templateID] : this.templates[info.templateID], context, true, true);
		}
	}

	/**
	 * getBackgroundImage
	 */
	getBackgroundImage(data: any): string {
		let info = this.panelInfo.find((x) => x.id == data.id);
		if (info) {
			return `url(${info.context.backgroundImage ?? ""})`;
		}
		return "";
	}

	/**
	 * toBase64
	 * @param file file 
	 * @returns 
	 */
	private toBase64(file: File): Promise<string | ArrayBuffer | null> {
		return new Promise<string | ArrayBuffer | null>((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = reject;
		});
	}

	/**
	 * panelBackgroundImageExtrasChanged
	 * @param event event 
	 * @param data data 
	 */
	panelBackgroundImageExtrasChanged(event: any, data: any) {
		let info = this.panelInfo.find((x) => x.id == data.id);
		if (info) {
			if (event.tile != undefined) {
				info.context.backgroundTile = event.tile;
			}
			if (event.fit != undefined) {
				info.context.fitToSize = event.fit;
			}
			const context = { ...info.context, barDefault: info.barDefault };
			this.renderTemplateIntoPanel(info.id, info.isHierarchy ? this.hierarchyTemplates[info.templateID] : this.templates[info.templateID], context, true, true);
		}
	}

	/**
	 * getPanelBackgroundImageExtras
	 * @param data data 
	 */
	getPanelBackgroundImageExtras(data: any): any {
		let info = this.panelInfo.find((x) => x.id == data.id);
		if (info) {
			return info.context;
		}
	}

	/**
	 * getSafeBackgroundURL
	 * @param data data 
	 * @returns 
	 */
	getSafeBackgroundURL(data: any) {
		return 'url(' + (data?.backgroundImage ?? '') + ')';
	}

	/**
	 * colourChange
	 * @param event event 
	 * @param data data 
	 */
	colourChange(event: any, text: boolean, data: any) {
		let info = this.panelInfo.find((x) => x.id == data.id);
		if (info) {
			if (text) {
				info.context.textColour = event.value;
			}
			const context = { ...info.context, barDefault: info.barDefault };
			this.renderTemplateIntoPanel(info.id, info.isHierarchy ? this.hierarchyTemplates[info.templateID] : this.templates[info.templateID], context, true, true);
		}
	}
}